import React, { FC, useState, useMemo } from 'react';
import { getSimpleDateFormat, isFCShopCategory } from 'utils/functions';
import { BrandType, SearchType } from 'utils/types';
import { Link } from 'react-router-dom';
import { MenuHorizontalIcon } from 'cokitchen-icons';
import { Dropdown, DropdownList, Input } from '@cokitchen/cokitchen-components';
import LiveTag from 'components/live-tag';
import { ENUM_BRAND_TYPE, ENUM_SHOP_STATUS } from 'utils/enum';
import { getBrandType } from 'utils/get-order-provider';
import { ROUTE_PREFIX } from 'utils/constants';
import BrandCardOptions, { BrandCardOptionType } from './brand-card-options';

type BrandCardProps = {
  data: BrandType | SearchType;
  isChecked?: boolean;
  className?: string;
  hideCheckbox?: boolean;
  options: BrandCardOptionType[];
  onClick: (type: BrandCardOptionType) => void;
  onCheck?: (arg0: any) => void;
  onMove?: (type: ENUM_BRAND_TYPE) => void;
};
const BrandCard: FC<BrandCardProps> = ({
  data,
  isChecked,
  className = '',
  hideCheckbox,
  options,
  onCheck,
  onClick,
  onMove
}) => {
  const { name, description, id, logo, updated_at, brand_type, partner_synced } = data;
  const [cardHovered, setCardHovered] = useState(false);

  const showOverlayOptions = useMemo(() => {
    if (hideCheckbox) return false;

    return isChecked || cardHovered;
  }, [hideCheckbox, isChecked, cardHovered]);

  const isFcShop = isFCShopCategory();
  const brandUrl = `${ROUTE_PREFIX.FOOD}/${['brand', id, 'details'].join('/')}`;
  const shopUrl = `${ROUTE_PREFIX.SHOP}/shop/${id}/details`;

  const detailsUrl = isFcShop ? shopUrl : brandUrl;
  const [showDropdown, setShowDropdown] = useState(false);

  const live = isFcShop ? data.status === ENUM_SHOP_STATUS.active : data.live;

  return (
    <div
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => setCardHovered(false)}
      onClick={() => onClick('select')}
      className={`flex flex-col relative hover:bg-[#F7F7F8] border border-1 z-0 rounded-[10px] border-[#EDEDEE] p-3 transition-height duration-1000 ${className}`}>
      {showOverlayOptions && (
        <div className='flex justify-between'>
          <Input
            type='checkbox'
            name='new'
            checked={isChecked}
            checkColor='red'
            onChange={onCheck}
            className='absolute top-3 left-4 z-[1000000000]'
          />
          {!isChecked && (
            <div className='flex justify-end absolute right-6 top-12  gap-2 items-center text-gray-5'>
              <Dropdown
                visible={showDropdown}
                content={
                  <div
                    onClick={() => setShowDropdown(!showDropdown)}
                    className='border border-[#E8E8E8] rounded-full p-2 hover:bg-gray-2'>
                    <MenuHorizontalIcon />
                  </div>
                }
                onClickOutside={() => setShowDropdown(false)}
                className='min-w-[199px]'>
                <DropdownList>
                  <BrandCardOptions
                    data={data}
                    options={options}
                    onClick={onClick}
                    onMove={onMove}
                  />
                </DropdownList>
              </Dropdown>
            </div>
          )}
        </div>
      )}
      <div className='flex gap-1 justify-between items-start'>
        <div className='relative p-0 m-0'>
          {getBrandType(data)}
          <img
            alt={name}
            className='w-16 h-16 rounded-full border border-[#E9ECEC] object-cover'
            src={logo}
          />
        </div>
        <LiveTag live={live} brandType={brand_type} partnerSynced={partner_synced} />
      </div>
      <p className='text-lg font-medium pt-2 pb-1'>{name}</p>
      <p className='grow text-[#858585] text-xs leading-5'> {description}</p>
      <hr className='my-3 text-[#DDDEDF]' />
      <p className='text-[10px] text-[#858585] pb-6'>
        Added {getSimpleDateFormat(updated_at ?? '')}
      </p>
      <Link to={detailsUrl}>
        <button className='w-full py-2 font-medium text-[#595959] border border-1 border-[#E8E8E8] rounded-[4px] text-sm'>
          View details
        </button>
      </Link>
    </div>
  );
};

export default BrandCard;
